/**
 * The stand-in for `authors-api` until it exists.
 *
 * M0 SHIPS NO OPERATION. The whole API is designed and specified and none of it is implemented, so
 * a screen built today has to talk to something. This module answers the four identity operations
 * with the SHAPES THE GENERATED TYPES DECLARE — every response below is typed as
 * `SchemaHandoffCode`, `SchemaHandoffExchangeResult` or `SchemaAuthorSession`, so a fixture that
 * drifts from the contract is a compile error rather than a surprise on the day the real API lands.
 *
 * IT ALSO REPRODUCES THE REFUSALS, because the refusals are the part of this flow that is easy to
 * get wrong and impossible to test against a happy path. In particular:
 *
 *   - UNKNOWN, EXPIRED AND ALREADY-USED ARE ONE ANSWER. "Redemption is one conditional UPDATE and
 *     single use is therefore a database fact ... Unknown, expired and already-used are
 *     deliberately indistinguishable — zero rows covers all three and the response says so with
 *     one answer" (docs/design/07-api/authors-api.yaml). The mock returns the identical
 *     401 / `HANDOFF_CODE_INVALID` body for all three, with no header and no field that could be
 *     used to tell them apart, so a screen CANNOT accidentally branch on something that will not
 *     be there. Milestone A1's acceptance script requires exactly this
 *     (docs/design/12-build-plan.md).
 *
 *   - A BODY CARRYING A UID IS A 400. `HandoffCodeRequest` and `HandoffExchangeRequest` are both
 *     `additionalProperties: false` with no uid property — "the absence is the control, not an
 *     ignore rule" (authors-api.yaml). The mock enforces it so the client is exercised
 *     against the real rule.
 *
 * DRIVING THE FAILURE STATES. `setMockScenario` (and, in a dev build, `?mx-mock=` on the URL) picks
 * which answer the next call gets. That is how the four named failure states are reachable in a
 * browser without a debugger and without a server.
 *
 * NO CLOCK IS READ HERE. Reading one is a lint error across this repo (C11: "read the injected
 * Clock, not the global"), and expiry needs no clock to be rendered correctly - an expired code
 * and a spent code are the same answer, so the fixture models "dead" and not "dead since".
 */
import type {
  SchemaAuthorSession,
  SchemaHandoffCode,
  SchemaHandoffCodeRequest,
  SchemaHandoffExchangeRequest,
  SchemaHandoffExchangeResult,
  SchemaProblem,
} from '../../../../../../shared/types/generated/authors-api';
import type { HttpRequest, HttpResponse, Transport } from './http';
import type { AuthorSession } from './authors-api';

/**
 * Which answer the mock gives. `healthy` is the default; the rest exist so each of the four named
 * failure states is a real rendered screen rather than a screenshot in a document.
 */
export type MockScenario =
  /** Everything works. */
  | 'healthy'
  /** Every hand-off code is refused: unknown, expired or already used — one answer for all three. */
  | 'handoff-code-dead'
  /** Google is down: 503 with `Retry-After` on all three (the exchange spends its code first). */
  | 'identity-unavailable'
  /** The rate limiter is refusing. 429 with the `Retry-After` it declares (45 seconds). */
  | 'rate-limited'
  /** The bearer token is rejected outright: 401 with `error="invalid_token"`. */
  | 'token-rejected'
  /** Signed in and applying (design 25): `reader`, no author row yet; `applicant`, with one. */
  | ApplyingScenario
  /** Approved but disabled: the cabinet stays reachable and says why. */
  | 'disabled-author';

let scenario: MockScenario = 'healthy';

export function setMockScenario(next: MockScenario): void {
  scenario = next;
}

export function currentMockScenario(): MockScenario {
  return scenario;
}

const MOCK_SCENARIOS: readonly MockScenario[] = [
  'healthy',
  'handoff-code-dead',
  'identity-unavailable',
  'rate-limited',
  'token-rejected',
  ...applyingScenarios(),
  'disabled-author',
];

/**
 * Lets an operator open `/enter?code=…&mx-mock=identity-unavailable` and see the real screen.
 * Called once by the transport seam; an unknown value is ignored rather than throwing, because a
 * mistyped debug parameter must not white-page the app.
 */
export function applyMockScenarioFromSearch(search: string): void {
  applyMockVideoAccessFromSearch(search);
  applyMockStreamAccessFromSearch(search);
  const requested = new URLSearchParams(search).get('mx-mock');
  if (requested === null) {
    return;
  }
  const match = MOCK_SCENARIOS.find((candidate) => candidate === requested);
  if (match !== undefined) {
    scenario = match;
  }
}

/**
 * A fixed instant, not a clock read. `expiresAt` is a `ServerTime` the PHONE app receives and the
 * cabinet never renders, so a frozen value is honest here and a clock read would be a lint error.
 */
const FIXTURE_EXPIRES_AT = '2026-08-27T12:00:00Z';

/** The code the fixture mints, and the only one it will exchange while `healthy`. */
export const FIXTURE_HANDOFF_CODE = 'fixture-handoff-code-0001';

/** Every code the fixture has already redeemed. Single use is a fact here too, not a convention. */
const redeemed = new Set<string>();

export function resetMockState(): void {
  redeemed.clear();
  scenario = 'healthy';
  videoAccess = DEFAULT_VIDEO_ACCESS;
  streamAccess = DEFAULT_STREAM_ACCESS;
}

/**
 * One RFC 9457 body, carrying the code the real API sends for the same refusal.
 *
 * `Problem.error_code` is REQUIRED on every problem body and is "the machine code a client
 * branches on. This is the contract" (docs/design/07-api/shared.yaml). When this fixture was
 * written the 46-code registry had no code for "malformed request", "rate limited", "this token is
 * invalid" or "cannot verify right now", so every such body borrowed `HANDOFF_CODE_INVALID` just to
 * stay schema-valid. All four exist now (decision-log rows 159 and 167; 65 members), and each body
 * below carries its own - `REQUEST_VALIDATION_FAILED`, `RATE_LIMITED`, `UNAUTHENTICATED`,
 * `UPSTREAM_UNAVAILABLE`, and `FRAMEWORK_REFUSAL` for a route the fixture does not serve - the ones
 * `src/common/errors/codeless-refusal.ts` gives the same refusals on the server, whose rule 2 is
 * that a borrowed near-miss code is worse than a vague one.
 *
 * Nothing in this feature branches on them: every screen branches on the HTTP status (failure.ts).
 */

function problem(
  status: number,
  errorCode: SchemaProblem['error_code'],
  title: string,
  detail: string,
): SchemaProblem {
  return {
    type: `https://moonx.art/problems/${errorCode.toLowerCase().replace(/_/g, '-')}`,
    title,
    status,
    detail,
    error_code: errorCode,
  };
}

function json(status: number, body: unknown, headers: Record<string, string> = {}): HttpResponse {
  return { status, headers: { 'content-type': 'application/json', ...headers }, body };
}

function problemResponse(
  status: number,
  body: SchemaProblem,
  headers: Record<string, string> = {},
): HttpResponse {
  return {
    status,
    headers: { 'content-type': 'application/problem+json', ...headers },
    body,
  };
}

/**
 * The one refusal that covers unknown, expired and already-used.
 *
 * It carries NO `WWW-Authenticate`: the inline 401 on `exchangeCabinetHandoffCode` declares no
 * headers at all (authors-api.yaml), and inventing one here would let a client learn
 * something the real API will not tell it.
 */
function handoffCodeInvalid(): HttpResponse {
  return problemResponse(
    401,
    problem(
      401,
      'HANDOFF_CODE_INVALID',
      'This link cannot be used',
      'The code is unknown, has expired, or has already been used.',
    ),
  );
}

function identityUnavailable(): HttpResponse {
  return problemResponse(
    503,
    problem(
      503,
      'UPSTREAM_UNAVAILABLE',
      'We cannot check your sign-in right now',
      'The identity provider could not be reached. This is not a problem with your account.',
    ),
    { 'retry-after': '20' },
  );
}

function rateLimited(): HttpResponse {
  return problemResponse(
    429,
    problem(
      429,
      'RATE_LIMITED', // what the server's `RateLimitedError` carries (src/common/errors/)
      'Too many attempts',
      'Please wait a little and try again.',
    ),
    // `Problem429` requires it since decision-log row 220; failure.spec reads a 429 without one.
    { 'retry-after': '45' },
  );
}

function tokenRejected(): HttpResponse {
  return problemResponse(
    401,
    problem(401, 'UNAUTHENTICATED', 'Your session has ended', 'Sign in again to continue.'),
    { 'www-authenticate': 'Bearer realm="moonx", error="invalid_token"' },
  );
}

const APPROVED_SESSION: SchemaAuthorSession = {
  authorId: 'fixture-author-0001',
  displayName: 'Mr Moon',
  authorStatus: 'approved',
  applicationState: 'approved',
  canApply: false,
  canUseEditor: true,
  cabinetReachable: true,
  termsAcceptanceRequired: false,
  slug: 'mr-moon',
  slugFrozen: true,
};

const READER_SESSION: SchemaAuthorSession = {
  authorId: null,
  displayName: null,
  authorStatus: 'applicant',
  applicationState: 'empty',
  // No author row yet (design 25): may apply or become an author, and reaches no cabinet screen.
  canApply: true,
  canUseEditor: false,
  cabinetReachable: false,
  termsAcceptanceRequired: false,
  slug: null,
  slugFrozen: false,
};

const DISABLED_SESSION: SchemaAuthorSession = {
  authorId: 'fixture-author-0002',
  displayName: 'Mr Moon',
  authorStatus: 'disabled',
  applicationState: 'approved',
  canApply: false,
  canUseEditor: false,
  // TRUE FOR DISABLED AUTHORS TOO. "A disabled author reaches the cabinet, sees their disabled
  // state, and has payout actions refused" (authors-api.yaml, LOCKED D34). Disabling an
  // author never touches sign-in.
  cabinetReachable: true,
  disabledReason:
    'Payouts are paused while we check a duplicate bank detail. Support has emailed you.',
  termsAcceptanceRequired: false,
  slug: 'mr-moon',
  slugFrozen: true,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `additionalProperties: false`, enforced. A uid here is a 400 and that is the control. */
function refuseUnknownProperties(body: unknown, allowed: readonly string[]): HttpResponse | null {
  if (!isRecord(body)) {
    return problemResponse(
      400,
      problem(
        400,
        'REQUEST_VALIDATION_FAILED',
        'Malformed request',
        'The request body is not an object.',
      ),
    );
  }
  const extra = Object.keys(body).filter((key) => !allowed.includes(key));
  if (extra.length > 0) {
    return problemResponse(
      400,
      problem(
        400,
        'REQUEST_VALIDATION_FAILED',
        'Malformed request',
        'The request body carries a property this operation does not accept.',
      ),
    );
  }
  return null;
}

function createHandoffCode(request: HttpRequest): HttpResponse {
  if (request.bearerToken === undefined) {
    return problemResponse(
      401,
      problem(
        401,
        'UNAUTHENTICATED', // the server's answer when no bearer token was presented at all
        'Sign in first',
        'This operation needs a Firebase ID token.',
      ),
      { 'www-authenticate': 'Bearer realm="moonx"' },
    );
  }

  const malformed = refuseUnknownProperties(request.body, ['client']);
  if (malformed !== null) {
    return malformed;
  }

  const body = request.body as SchemaHandoffCodeRequest;
  if (body.client !== 'ios' && body.client !== 'android' && body.client !== 'web') {
    return problemResponse(
      400,
      problem(
        400,
        'REQUEST_VALIDATION_FAILED',
        'Malformed request',
        '`client` is not a known client kind.',
      ),
    );
  }

  if (scenario === 'identity-unavailable') {
    return identityUnavailable();
  }
  if (scenario === 'rate-limited') {
    return rateLimited();
  }
  if (scenario === 'token-rejected') {
    return tokenRejected();
  }

  const minted: SchemaHandoffCode = {
    code: FIXTURE_HANDOFF_CODE,
    expiresAt: FIXTURE_EXPIRES_AT,
    // "120 at v1, and a config value rather than a constant."
    expiresInSeconds: 120,
  };
  return json(201, minted);
}

function exchangeHandoffCode(request: HttpRequest): HttpResponse {
  const malformed = refuseUnknownProperties(request.body, ['code']);
  if (malformed !== null) {
    return malformed;
  }

  const body = request.body as SchemaHandoffExchangeRequest;
  if (typeof body.code !== 'string' || body.code.length < 16 || body.code.length > 128) {
    return problemResponse(
      400,
      problem(
        400,
        'REQUEST_VALIDATION_FAILED',
        'Malformed request',
        '`code` is missing or the wrong length.',
      ),
    );
  }

  if (scenario === 'rate-limited') {
    return rateLimited();
  }
  if (scenario === 'handoff-code-dead') {
    return handoffCodeInvalid();
  }

  if (body.code !== FIXTURE_HANDOFF_CODE || redeemed.has(body.code)) {
    return handoffCodeInvalid();
  }

  // Single use is a database fact. Here it is a Set, which is the same fact with a smaller table.
  redeemed.add(body.code);
  // A failed mint's 503 comes AFTER the redemption, as on the server (row 223): the code is spent.
  if (scenario === 'identity-unavailable') {
    return identityUnavailable();
  }

  const result: SchemaHandoffExchangeResult = {
    customToken: `fixture-custom-token-for-${body.code}`,
  };
  return json(200, result);
}

function authorSession(request: HttpRequest): HttpResponse {
  if (request.bearerToken === undefined) {
    return problemResponse(
      401,
      problem(
        401,
        'UNAUTHENTICATED', // the server's answer when no bearer token was presented at all
        'Sign in first',
        'This operation needs a Firebase ID token.',
      ),
      { 'www-authenticate': 'Bearer realm="moonx"' },
    );
  }
  if (scenario === 'identity-unavailable') {
    return identityUnavailable();
  }
  if (scenario === 'rate-limited') {
    return rateLimited();
  }
  if (scenario === 'token-rejected') {
    return tokenRejected();
  }

  const session: AuthorSession =
    scenario === 'reader' || scenario === 'applicant'
      ? sessionWhileApplying(scenario)
      : scenario === 'disabled-author'
        ? { ...DISABLED_SESSION, canWriteDrafts: false, videoAccess, streamAccess }
        : { ...APPROVED_SESSION, canWriteDrafts: true, videoAccess, streamAccess };

  // `GET /v1/me` is declared `Cache-Control: no-store` — a cached session read would show a
  // disabled author their old capabilities.
  return json(200, session, { 'cache-control': 'no-store' });
}

export const mockTransport: Transport = (request: HttpRequest): Promise<HttpResponse> => {
  if (request.method === 'POST' && request.path === '/v1/handoff/codes') {
    return Promise.resolve(createHandoffCode(request));
  }
  if (request.method === 'POST' && request.path === '/v1/handoff/exchange') {
    return Promise.resolve(exchangeHandoffCode(request));
  }
  if (request.method === 'GET' && request.path === '/v1/me') {
    return Promise.resolve(authorSession(request));
  }
  if (request.method === 'POST' && request.path === '/v1/authorship') {
    return Promise.resolve(becomeAuthor(request));
  }
  if (request.method === 'POST' && request.path === '/v1/video-access') {
    return Promise.resolve(requestVideoAccess(request));
  }
  if (request.method === 'POST' && request.path === '/v1/stream-access') {
    return Promise.resolve(requestStreamAccess(request));
  }

  return Promise.resolve(
    problemResponse(
      404,
      problem(
        404,
        'FRAMEWORK_REFUSAL',
        'Not implemented in the fixture',
        `${request.method} ${request.path} is not one of the four identity operations this fixture answers.`,
      ),
    ),
  );
};

/* ==================================================== applying: a reader, and an applicant */

/**
 * ★ TWO SCENARIOS WHERE THERE WAS ONE (design 25, section 3.4). `applicant` used to be the
 * signed-in person with NO author row, who could reach no cabinet screen. Design 25 tells them
 * apart: `reader` is that person (`READER_SESSION`, above), offered "Become an author"; `applicant`
 * has the row and enters the cabinet, empty, and writes drafts - but the editor's Submit waits for
 * approval. Declared at the foot so no line cited above moved; the union and the list above read
 * them through a type and a hoisted function.
 */
type ApplyingScenario = 'reader' | 'applicant';

function applyingScenarios(): readonly ApplyingScenario[] {
  return ['reader', 'applicant'];
}

/**
 * An applicant with a row: `authorId` set, the application still `empty`, the cabinet open, drafts
 * allowed and the editor's Submit not (`canUseEditor`). `termsAcceptanceRequired` is "approved and
 * owing a submission document", so it is never true here: an applicant accepts inside the
 * application (design 25, 3.4).
 */
const APPLICANT_SESSION: AuthorSession = {
  authorId: 'fixture-author-0003',
  displayName: null,
  authorStatus: 'applicant',
  applicationState: 'empty',
  canApply: true,
  canUseEditor: false,
  canWriteDrafts: true,
  cabinetReachable: true,
  termsAcceptanceRequired: false,
  slug: null,
  slugFrozen: false,
};

/**
 * The two sessions while applying. `canWriteDrafts` (design 25) is added here and in
 * `authorSession` rather than inside the constants above, whose lines are cited by number.
 */
function sessionWhileApplying(which: ApplyingScenario): AuthorSession {
  return which === 'reader' ? { ...READER_SESSION, canWriteDrafts: false } : APPLICANT_SESSION;
}

/**
 * `POST /v1/authorship`: the reader becomes an applicant, and the fixture remembers it - the next
 * `GET /v1/me` is the applicant's - and everybody else is answered with their own session
 * (idempotent). Every refusal is `GET /v1/me`'s own: no token, an outage, the limiter, a token
 * rejected.
 */
function becomeAuthor(request: HttpRequest): HttpResponse {
  if (scenario === 'reader' && request.bearerToken !== undefined) {
    scenario = 'applicant';
  }
  return authorSession(request);
}

/* ============================================================== video access (v1.0.29) */

type VideoAccess = NonNullable<AuthorSession['videoAccess']>;

/**
 * Granted on this desk, so the default build shows the video editor; `?mx-video=none` (or
 * `requested`) shows the "+ New" menu's other rows. Declared at the foot so no line cited above
 * moved; `resetMockState` and `authorSession` read it at call time.
 */
const DEFAULT_VIDEO_ACCESS: VideoAccess = 'granted';
let videoAccess: VideoAccess = DEFAULT_VIDEO_ACCESS;

export function setMockVideoAccess(next: VideoAccess): void {
  videoAccess = next;
}

function applyMockVideoAccessFromSearch(search: string): void {
  const requested = new URLSearchParams(search).get('mx-video');
  if (requested === 'none' || requested === 'requested' || requested === 'granted') {
    videoAccess = requested;
  }
}

/**
 * `POST /v1/video-access`: an approved author's ask is recorded once (`none` becomes `requested`);
 * asking again, or with access granted, changes nothing. Answers the session, with `GET /v1/me`'s
 * own refusals - and, as the platform's capability guard does, refuses anyone who is not an
 * approved author with the 403 of the state they are in (`notApprovedRefusal`).
 */
function requestVideoAccess(request: HttpRequest): HttpResponse {
  if (request.bearerToken !== undefined) {
    const refused = notApprovedRefusal();
    if (refused !== null) {
      return refused;
    }
  }
  if (request.bearerToken !== undefined && scenario === 'healthy' && videoAccess === 'none') {
    videoAccess = 'requested';
  }
  return authorSession(request);
}

/* ============================================================= stream access (1.2.0) */

type StreamAccess = NonNullable<AuthorSession['streamAccess']>;

/** Granted on this desk as video is; `?mx-stream=none` (or `requested`) shows the other rows. */
const DEFAULT_STREAM_ACCESS: StreamAccess = 'granted';
let streamAccess: StreamAccess = DEFAULT_STREAM_ACCESS;

export function setMockStreamAccess(next: StreamAccess): void {
  streamAccess = next;
}

function applyMockStreamAccessFromSearch(search: string): void {
  const requested = new URLSearchParams(search).get('mx-stream');
  if (requested === 'none' || requested === 'requested' || requested === 'granted') {
    streamAccess = requested;
  }
}

/** `POST /v1/stream-access`: asked, refused and answered exactly as `POST /v1/video-access`. */
function requestStreamAccess(request: HttpRequest): HttpResponse {
  if (request.bearerToken !== undefined) {
    const refused = notApprovedRefusal();
    if (refused !== null) {
      return refused;
    }
  }
  if (request.bearerToken !== undefined && scenario === 'healthy' && streamAccess === 'none') {
    streamAccess = 'requested';
  }
  return authorSession(request);
}

/**
 * The capability guard's three refusals (src/common/guards/author-capability.guard.ts): a disabled
 * author, an applicant, and a reader with no author row. `null` for every other scenario.
 */
function notApprovedRefusal(): HttpResponse | null {
  if (scenario === 'disabled-author') {
    return problemResponse(
      403,
      problem(
        403,
        'AUTHOR_DISABLED',
        'This author account is disabled',
        'This author account is disabled. The cabinet is still reachable; the editor is not.',
      ),
    );
  }
  if (scenario === 'applicant') {
    return problemResponse(
      403,
      problem(
        403,
        'AUTHOR_APPROVAL_REQUIRED',
        'Your author application is approved first',
        'This needs an approved author.',
      ),
    );
  }
  if (scenario === 'reader') {
    return problemResponse(
      403,
      problem(
        403,
        'AUTHOR_CAPABILITY_REQUIRED',
        'You are not an author yet',
        'This needs an author account.',
      ),
    );
  }
  return null;
}
